import React from 'react';
import proptypes from 'prop-types';
import {
  params,
  arrayOf as arrayType,
  string as stringType,
  bool as boolType,
  fragment,
  shape,
  customType,
  useDataModel,
  typename
} from '@thd-nucleus/data-sources';
import { ProductShelfRenderer } from './ProductShelfRenderer';
import { normalizeProductShelfData } from '../utils/normalizeProductShelfData';

const { string, bool } = proptypes;

const ProductShelf = ({
  componentId,
  componentClass,
  slotContentId: slotName = '',
  mediaCollectionIndex = 0,
  showTooFewProductsNotice,
  theme,
  homepageProductShelf,
  minDpdCount = 4
}) => {
  const { data: componentData, loading: componentLoading, error: componentError } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass
    },
    skip: !!slotName
  });

  const { data: slotData, loading: slotLoading, error: slotError } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: { id: slotName },
    skip: !slotName
  });

  const normalizedData = normalizeProductShelfData({
    slotData,
    componentData,
    mediaCollectionIndex
  });

  const isLoading = slotName ? slotLoading : componentLoading;
  const hasError = slotName ? slotError : componentError;

  if (isLoading || hasError || !normalizedData) {
    return null;
  }

  return (
    <ProductShelfRenderer
      componentId={normalizedData.id || componentId}
      dataComponent={normalizedData}
      showTooFewProductsNotice={showTooFewProductsNotice}
      theme={normalizedData.theme || theme}
      homepageProductShelf={homepageProductShelf}
      minDpdCount={minDpdCount}
    />
  );
}

ProductShelf.propTypes = {
  componentId: string,
  componentClass: string,
  slotContentId: string,
  mediaCollectionIndex: proptypes.number,
  showTooFewProductsNotice: bool,
  theme: string,
  homepageProductShelf: bool
};

ProductShelf.defaultProps = {
  componentId: '',
  componentClass: 'ProductShelf',
  slotContentId: '',
  mediaCollectionIndex: 0,
  showTooFewProductsNotice: false,
  theme: null,
  homepageProductShelf: false
};

ProductShelf.displayName = 'ProductShelf'

ProductShelf.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['ProductShelf']).isRequired()
  }).shape({
    ProductShelf: fragment().shape({
      id: stringType(),
      componentName: stringType(),
      sortBy: stringType(),
      publishDate: stringType(),
      sortOrderOptions: stringType(),
      theme: stringType(),
      title: stringType(),
      productShelfData: shape({
        navParam: stringType(),
        skus: arrayType(stringType())
      })
    })
  }),
  slot: params({
    id: stringType(),
  }).shape({
    id: stringType(),
    entryId: stringType(),
    mediaCollections: arrayType(
      shape({
        entryId: stringType(),
        theme: stringType(),
        title: stringType(),
        media: arrayType(
          shape({
            entryId: stringType(),
            customProps: stringType(),
          })
        ),
      })
    ),
    __typename: typename('newSlot'),
  }),
};

export { ProductShelf };
